import React, { useEffect, useState } from "react";
import WebmasterSidebar from "../../components/WebmasterSidebar";
import API_BASE_URL from "../../config/apiConfig";
import { useAuth } from "../../context/AuthProvider";
import { useNavigate } from "react-router-dom";
import teamIcon from "../../assets/images/users-inactive.svg";
import appointmentsIcon from "../../assets/images/calendar.svg";
import medicIcon from "../../assets/images/medic.svg";
import trophy from "../../assets/images/trophy-theme.svg";
import faceHappy from "../../assets/images/face-happy.svg";

interface Therapist {
  user_id: string;
  firstname: string;
  lastname: string;
  is_active: boolean;
}

interface Medic {
  user_id: string;
  firstname: string;
  lastname: string;
  is_active: boolean;
}

interface Patient {
  id: string;
  is_active: boolean;
}

interface ScoreEntry {
  therapist_id: string;
  total_score: number;
}

interface NamedScoreEntry extends ScoreEntry {
  fullname: string;
}

const getCurrentMonthRange = () => {
  const today = new Date();
  const start = new Date(today.getFullYear(), today.getMonth(), 1);
  const end = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  const toDateString = (d: Date, time: "start" | "end") => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const datePart = `${year}-${month}-${day}`;
    return time === "start"
      ? `${datePart}T00:00:00`
      : `${datePart}T23:59:59.999`;
  };

  return {
    start_time: toDateString(start, "start"),
    end_time: toDateString(end, "end"),
  };
};

const WebmasterDashboard: React.FC = () => {
  const { token } = useAuth();
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    return localStorage.getItem("sidebarCollapsed") === "true";
  });

  const [activeTherapistsCount, setActiveTherapistsCount] = useState<number>(0);
  const [activePatientsCount, setActivePatientsCount] = useState<number>(0);
  const [activeMedicsCount, setActiveMedicsCount] = useState<number>(0);
  const [topTherapists, setTopTherapists] = useState<NamedScoreEntry[]>([]);
  const [todayAppointments, setTodayAppointments] = useState<number>(0);
  const todayDate = useState(formatDateToYMD(new Date()));
  const navigate = useNavigate();

  function formatDateToYMD(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }


  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Terapeuti activi
        const therapistRes = await fetch(`${API_BASE_URL}/users/therapists`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const therapists: Therapist[] = await therapistRes.json();
        setActiveTherapistsCount(therapists.filter((t) => t.is_active).length);

          // Medici activi
          const medicRes = await fetch(`${API_BASE_URL}/users/medics`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const medics: Medic[] = await medicRes.json();
          setActiveMedicsCount(medics.filter((t) => t.is_active).length);

        // Pacienti activi
        const patientRes = await fetch(`${API_BASE_URL}/patient/get-all`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const patients: Patient[] = await patientRes.json();
        setActivePatientsCount(patients.filter((p) => p.is_active).length);

        // Top terapeuti luna curentă
        const { start_time, end_time } = getCurrentMonthRange();
        const scoresRes = await fetch(
          `${API_BASE_URL}/score-config/total-by-therapist?start_time=${encodeURIComponent(
            start_time
          )}&end_time=${encodeURIComponent(end_time)}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const scores: ScoreEntry[] = await scoresRes.json();

        const sorted = Array.isArray(scores)
          ? scores.sort((a, b) => b.total_score - a.total_score)
          : [];

        // Fetch nume pentru fiecare terapeut
        const withNames: NamedScoreEntry[] = await Promise.all(
          sorted.map(async (entry) => {
            try {
              const nameRes = await fetch(
                `${API_BASE_URL}/users/therapist/${entry.therapist_id}`,
                {
                  headers: { Authorization: `Bearer ${token}` },
                }
              );
              const therapist = await nameRes.json();
              return {
                ...entry,
                fullname: `${therapist.firstname} ${therapist.lastname}`,
              };
            } catch (err) {
              console.error("Eroare la fetch nume terapeut:", err);
              return {
                ...entry,
                fullname: entry.therapist_id, // fallback
              };
            }
          })
        );

        setTopTherapists(withNames);
      } catch (error) {
        console.error("Eroare la încărcarea datelor de dashboard:", error);
      }
    };

    fetchStats();
     if (!token) return;

  const controller = new AbortController();
  fetchTodayAppointments(controller.signal);

  return () => controller.abort(); 
  }, [token]);

    const fetchTodayAppointments = async (signal?: AbortSignal) => {
      const todayStr = formatDateToYMD(new Date());

      try {
        const res = await fetch(
          `${API_BASE_URL}/appointments/get-appointments?date=${todayStr}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            signal,                         // ← permite anularea dacă ai nevoie
          }
        );

        if (!res.ok) throw new Error("Nu s-au putut prelua programările.");
        const data = await res.json();

        setTodayAppointments(data.data.length);   // deja filtrate pe server
      } catch (err) {
        if (err instanceof DOMException && err.name === "AbortError") {
          console.error("Eroare la programările de azi:", err);
        }
      }
    };

  return (
    <div className={`dashboard-container ${isCollapsed ? "collapsed" : ""}`}>
      <WebmasterSidebar
        onCollapseChange={(collapsed) => setIsCollapsed(collapsed)}
      />
      <div className="dashboard-content">
        <h1 className="user-welcome"><img src={faceHappy} alt="user" /> <span className="color-dark">Bine ai venit,</span> Webmaster</h1>

        <div className="dashboard-widgets">
          <div className="widget">
            <h3><img src={teamIcon} alt="Echipă" /> Terapeuți activi</h3>
            <p className="widget-number">{activeTherapistsCount}</p>
            <button onClick={() => navigate("/webmaster/therapists")} className="widget-btn">Vezi terapeuți</button>
          </div>
          <div className="widget">
            <h3><img src={medicIcon} alt="Echipă" /> Medici activi</h3>
            <p className="widget-number">{activeMedicsCount}</p>
            <button onClick={() => navigate("/webmaster/medics")} className="widget-btn">Vezi medici</button>
          </div>
          <div className="widget">
            <h3><img src={teamIcon} alt="Echipă" /> Pacienți activi</h3>
            <p className="widget-number">{activePatientsCount}</p>
            <button onClick={() => navigate("/webmaster/patients")} className="widget-btn">Vezi pacienți</button>
          </div>
          <div className="widget">
            <h3><img src={appointmentsIcon} alt="Echipă" /> Programări azi</h3>
            <p className="widget-number">{todayAppointments}</p>
            <button onClick={() => navigate("/webmaster/therapists/appointments")} className="widget-btn">Vezi programări</button>
          </div>
        </div>

        <div className="top-therapists-section">
          <h2><img src={trophy} alt="trofeu" /> Top 5 Terapeuți (luna curentă)</h2>
          {topTherapists.length === 0 ? (
            <p>Nu există scoruri disponibile.</p>
          ) : (
            <ul className="top-therapists-list">
              {topTherapists.map((therapist, index) => (
                <li key={therapist.therapist_id}>
                  <span className="rank">#{index + 1}</span>{" "}
                  <span className="name">{therapist.fullname}</span>{" "}
                  <span className="score">{therapist.total_score} puncte</span>
                </li>
              ))}
            </ul>
          )}
          <button onClick={() => navigate("/webmaster/standings/")} className="primary-btn">Vezi mai mult</button>
        </div>
      </div>
    </div>
  );
};

export default WebmasterDashboard;
